import {
  workflowProgressIconSelector,
  workflowIconOffsets,
} from '../../constants'

import type { WorkflowProgressData } from '../../types'

import pipe from 'lodash/fp/pipe'

type IconData = {
  wrapper: HTMLElement
  specificClasses?: Array<string>
  offset?: number
  apiObj?: WorkflowProgressData
  tooltipTitleKey?: string
}

const ensureIconWrapper = (cell: HTMLElement): HTMLElement => {
  const wrapper = cell.querySelector('.icons-wrapper') as HTMLElement
  if (wrapper) {
    return wrapper
  }

  const newWrapper = document.createElement('div')
  newWrapper.classList.add(
    'tw-absolute',
    'tw-left-0',
    'tw-top-2',
    'icons-wrapper'
  )
  cell.append(newWrapper)

  return newWrapper
}

const renderIcon = ({
  wrapper,
  specificClasses = [],
  tooltipTitleKey,
  offset = 0,
}: IconData): HTMLElement => {
  const icon = document.createElement('span')
  const classes = ['tw-absolute', 'tw-left-0']

  icon.classList.add(...classes, ...specificClasses.filter(Boolean))
  icon.style.marginLeft = `${offset}px`

  wrapper.append(icon)

  // this should only be true for an icon, not spinner
  if (tooltipTitleKey) {
    icon.title = window.I18n.t(tooltipTitleKey)
    icon.dataset.toggle = 'tooltip'
  }

  return icon
}

const addOffset =
  (offset: number) =>
  (data: IconData): IconData => ({
    ...data,
    offset,
  })

const addSpecificClasses =
  (classes: Array<string>) =>
  (data: IconData): IconData => ({
    ...data,
    specificClasses: classes,
  })

const addTooltipTitle =
  (tooltipTitleKey: string) =>
  (data: IconData): IconData => {
    return {
      wrapper: data.wrapper,
      specificClasses: data.specificClasses,
      tooltipTitleKey: tooltipTitleKey,
      offset: data.offset,
    }
  }

export const addReconciledClassesAndTooltipTitle = (
  data: IconData
): IconData => {
  const { apiObj } = data

  if (!apiObj) {
    return data
  }

  let classes = ['sf-c-danger', 'i-unreconciled']
  let tooltipTitleKey

  if (
    apiObj.object_type === 'reconciliation_text' &&
    apiObj.reconciliation_not_necessary
  ) {
    classes = ['i-no-reconciliation-needed']
    tooltipTitleKey = 'tooltips.reconciliation.icons.data_entered'

    if (!apiObj.has_text_properties) {
      classes.push('no-text-properties')
      tooltipTitleKey = 'tooltips.reconciliation.icons.no_data_entered'
    }
  } else if (apiObj.reconciled) {
    classes = ['i-reconciled', 'sf-c-success']
    tooltipTitleKey = 'tooltips.reconciliation.icons.complete'
  } else {
    classes = ['i-unreconciled', 'sf-c-danger']
    tooltipTitleKey = 'tooltips.reconciliation.icons.action_needed'
  }

  return {
    ...data,
    specificClasses: [...classes, workflowProgressIconSelector],
    tooltipTitleKey: tooltipTitleKey,
  }
}

const addTooltip =
  (tooltip?: string) =>
  (icon: HTMLElement): HTMLElement => {
    if (!tooltip) {
      return icon
    }

    icon.dataset.toggle = 'tooltip'
    icon.title = tooltip
    return icon
  }

const renderReconciled = pipe(
  addOffset(workflowIconOffsets.RECONCILED),
  addReconciledClassesAndTooltipTitle,
  renderIcon
)

const renderNotes = pipe(
  addOffset(workflowIconOffsets.NOTES),
  addSpecificClasses(['i-comment-solid', workflowProgressIconSelector]),
  addTooltipTitle('tooltips.reconciliation.icons.note_added'),
  renderIcon
)

const renderTodos = pipe(
  addOffset(workflowIconOffsets.TODOS),
  addSpecificClasses(['i-bolt', 'sf-c-success', workflowProgressIconSelector]),
  addTooltipTitle('tooltips.reconciliation.icons.todo_added'),
  renderIcon
)

const renderChecks = pipe(
  addOffset(workflowIconOffsets.CHECKS),
  addSpecificClasses([
    'i-square',
    'sf-c-primary',
    workflowProgressIconSelector,
  ]),
  addTooltipTitle('tooltips.reconciliation.icons.check_assigned'),
  renderIcon
)

export const addSpinner = (cell?: HTMLElement): void => {
  if (!cell) {
    return
  }
  const wrapper = ensureIconWrapper(cell)
  const existingSpinner = wrapper.querySelector('.sf-spinner') as HTMLElement

  if (existingSpinner) {
    return
  }

  wrapper
    .querySelectorAll(`.${workflowProgressIconSelector}`)
    .forEach((icon) => icon.remove())

  pipe(
    addOffset(workflowIconOffsets.RECONCILED),
    addSpecificClasses(['sf-spinner']),
    renderIcon
  )({ wrapper })
}

export const clearSpinner = (cell?: HTMLElement): void => {
  if (!cell) {
    return
  }
  const spinner = cell.querySelector('.sf-spinner')
  spinner?.remove()
}

export const renderProposalIcon = ({
  cell,
  tooltip,
}: {
  cell?: HTMLElement
  tooltip?: string
} = {}): void => {
  if (!cell) {
    return
  }
  const wrapper = ensureIconWrapper(cell)
  pipe(
    addOffset(workflowIconOffsets.PROPOSAL),
    addSpecificClasses(['icon', 'icon-edit', 'sf-c-text-darker']),
    renderIcon,
    addTooltip(tooltip)
  )({ wrapper })
}

export const renderReviewIcon = ({
  cell,
  tooltip,
  classes,
}: {
  cell?: HTMLElement
  tooltip?: string
  classes?: Array<string> | null
} = {}): void => {
  if (!cell) {
    return
  }
  const wrapper = ensureIconWrapper(cell)

  pipe(
    addOffset(workflowIconOffsets.REVIEW),
    addSpecificClasses([
      'icon',
      'icon-check',
      'sf-c-text-dark',
      ...(classes || []),
    ]),
    renderIcon,
    addTooltip(tooltip)
  )({ wrapper })
}

export const renderMissingReconciliationIcon = (cell: HTMLElement): void => {
  const iconsWrapper = ensureIconWrapper(cell)

  renderIcon({
    wrapper: iconsWrapper,
    specificClasses: [
      'i-no-reconciliation-needed',
      'no-text-properties',
      'workflow-progress-icon',
    ],
    tooltipTitleKey: 'tooltips.reconciliation.icons.starred_missing_data',
    offset: 16,
  })
}

export const renderWorkflowProgressIcons = ({
  cell,
  apiObj,
}: {
  cell?: HTMLElement
  apiObj?: WorkflowProgressData
}): void => {
  if (!cell || !apiObj) {
    return
  }

  clearSpinner(cell)

  const wrapper = ensureIconWrapper(cell)
  const data = { wrapper, apiObj }

  if (apiObj.starred) {
    renderReconciled(data)
  }
  if (apiObj.has_notes) {
    renderNotes(data)
  }
  if (apiObj.has_todos) {
    renderTodos(data)
  }
  if (apiObj.has_checks) {
    renderChecks(data)
  }
}
